import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { CheckCircle, Clock, XCircle, ArrowLeft, Eye } from 'lucide-react';

export default function BoostSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, tc } = useTranslation('boost');
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [listingId, setListingId] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }
    const checkStatus = async () => {
      try {
        const res = await api.get(`/boost/status/${sessionId}`);
        setStatus(res.data.status || 'success');
        setListingId(res.data.listing_id);
      } catch {
        setStatus('pending');
      }
    };
    checkStatus();
    const interval = setInterval(checkStatus, 3000);
    return () => clearInterval(interval);
  }, [sessionId]);

  if (status === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-center" data-testid="boost-verifying">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1F3F72] mb-4" />
        <p className="text-lg font-semibold text-neutral-700">{t('verifyingPayment')}</p>
        <p className="text-sm text-neutral-500 mt-1">{t('pleaseWait')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-16 text-center" data-testid="boost-success-page">
      {status === 'success' && (
        <>
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-6">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-neutral-900 mb-3">{t('successTitle')}</h1>
          <p className="text-sm text-neutral-600 mb-8">{t('successMsg')}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {listingId && (
              <Button className="bg-[#F25C05] hover:bg-[#d94f04] text-white" onClick={() => navigate(`/annonces/${listingId}`)} data-testid="view-listing-btn">
                <Eye className="w-4 h-4 mr-2" /> {t('viewListing')}
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate('/tableau-de-bord')} data-testid="back-dashboard-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> {t('backDashboard')}
            </Button>
          </div>
        </>
      )}

      {status === 'pending' && (
        <>
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 mb-6">
            <Clock className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-neutral-900 mb-3">{t('pendingTitle')}</h1>
          <p className="text-sm text-neutral-600 mb-8">{t('pendingMsg')}</p>
          <Button variant="outline" onClick={() => navigate('/tableau-de-bord')} data-testid="pending-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> {t('backDashboard')}
          </Button>
        </>
      )}

      {status === 'failed' && (
        <>
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-6">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-neutral-900 mb-3">{t('failedTitle')}</h1>
          <p className="text-sm text-neutral-600 mb-8">{t('failedMsg')}</p>
          <Button variant="outline" onClick={() => navigate('/tableau-de-bord')} data-testid="failed-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> {t('backDashboard')}
          </Button>
        </>
      )}
    </div>
  );
}
